import React from "react";
import { TimeView } from "./TimeView";
import { AttributesDisplay } from "./AttributesDisplay";
import { hasShiftSubmissionTimeEnded, TimestampAttributeName } from "../../../shiftUtils";
import { PayrollHoursStatus, PayrollStatusReason } from "../../../types/Payroll.types";
import { ShiftBreak, ShiftComponent, TimesheetsShiftInfo } from "../../../types/Shift.types";

type BreakDisplayProps = {
  shiftBreak: ShiftBreak;
  shiftInfo: TimesheetsShiftInfo;
  shiftKey: string;
  showMessaging?: boolean;
};

export const BreakDisplay = ({
  shiftBreak,
  shiftInfo,
  shiftKey,
  showMessaging,
}: BreakDisplayProps) => {
  const { userShift, shiftThresholds } = shiftInfo;
  const { status, statusReason } = userShift;

  const getFormattedDuration = () => {
    const { breakTimestamps, durationMinutes } = shiftBreak;

    const hasValidIndividualDurations =
      breakTimestamps.length > 0 &&
      breakTimestamps.every((bt) => typeof bt.durationMinutes === "number");

    if (hasValidIndividualDurations) {
      if (breakTimestamps.length === 2) {
        return `${breakTimestamps[0].durationMinutes!.toFixed(
          0,
        )} min / ${breakTimestamps[1].durationMinutes!.toFixed(0)} min`;
      }
      if (breakTimestamps.length >= 3) {
        return `${durationMinutes.toFixed(0)} min total (${breakTimestamps.length}+ breaks)`;
      }
    }

    return `${durationMinutes.toFixed(0)} min`;
  };

  const formattedDuration = getFormattedDuration();
  let formattedDurationInDispute: string | undefined;

  if (shiftBreak.isDisputedDurationMissing) {
    formattedDurationInDispute = "Missing";
  } else if (shiftBreak.durationInDispute !== undefined) {
    formattedDurationInDispute = `${shiftBreak.durationInDispute} min`;
  }

  const isCompleteShiftInDispute =
    status === PayrollHoursStatus.DISPUTED &&
    !!statusReason &&
    ![PayrollStatusReason.SUPPORT_UNADJUSTED, PayrollStatusReason.RECEIVED_BREAKS].includes(
      statusReason,
    );

  return (
    <>
      <TimeView
        formattedTime={formattedDuration}
        isCompleteShiftInDispute={isCompleteShiftInDispute}
        formattedTimeInDispute={formattedDurationInDispute}
        isTimestampSubmitted={shiftBreak.attributes.includes(TimestampAttributeName.CM_SUBMITTED)}
        missingTimeTooltipMessage={"Meal break is non-compliant and/or missing"}
        breakNonCompliantTooltipMessage={"Meal break is non-compliant and/or missing"}
        isBreakNonCompliant={shiftBreak.attributes.includes(
          TimestampAttributeName.BREAK_NON_COMPLIANT
        )}
        isTimestampMissing={shiftBreak.attributes.includes(
          TimestampAttributeName.CM_MISSING_SUBMISSION,
        )}
        hasShiftSubmissionTimeEnded={hasShiftSubmissionTimeEnded(shiftThresholds)}
        tooltipAnchor={`break-time-${shiftKey}`}
      />
      <AttributesDisplay
        attributes={userShift.break.attributes}
        shiftInfo={shiftInfo}
        timeType={ShiftComponent.BREAK_DURATION}
        tooltipAnchor={`break-attribute-${shiftKey}`}
        showMessaging={showMessaging}
      />
    </>
  );
};
